import { defineComponent as _defineComponent } from 'vue'

import type { MetaInfo } from 'vue-meta';

const __default__ = {
	name: 'App',
	metaInfo(): MetaInfo {
		return (this as unknown as { head: MetaInfo }).head;
	},
};

import '@/styles/global.scss';
import '@fortawesome/fontawesome-svg-core/styles.css';
import Vue, { computed, getCurrentInstance, nextTick, onMounted } from 'vue';

import { Platform } from './@types/graphql-types';

import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { enableScroll } from '@/helpers/scroll-helper';
//import { getVm } from '@/helpers/vm-helper';
import { until } from '@vueuse/core';
import { useMutation } from '@/helpers/composables/useApollo';
import { useRoute, useRouter } from 'vue-router/composables';
import { useLanguageStore } from './stores/pinia/useLanguageStore';
import { useUserStats } from '@/helpers/composables/useUserStats';
import { useLoginFlow } from '@/features/auth/composables/useLoginFlow';
import { useMetaStore } from './stores/pinia/useMetaStore';
import { storeToRefs } from 'pinia';
import { useRoutingStore } from './stores/pinia/useRoutingStore';
import ContentSSR from '@/components/ContentSSR.vue';
// @ts-expect-error Could not find a declaration file for module 'vue-lazy-hydration'.
import LazyHydrate from 'vue-lazy-hydration';
// @ts-expect-error Could not find a declaration file for module 'vue-no-ssr'.
import NoSSR from 'vue-no-ssr';

import CanaryMarker from '@/ui-components/CanaryMarker.vue';
import AppFooter from '@/components/footer/AppFooter.vue';
import AppHeader from '@/components/nav/AppHeader.vue';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import StyleInjector from '@/components/StyleInjector';
import { watch } from 'vue';
import {
	RegisterSessionStartDocument,
	RegisterSessionStartMutation,
	RegisterSessionStartMutationVariables,
} from '@/graphql/mutation/RegisterSessionStart.mutation';
import { getVm } from './helpers/vm-helper';
import { initLanguage } from './main.ssr';
import { NewExperimentsType, useExperimentInitialization } from '@/libraries/experiment';

// make NoSSR a globaly available component

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose }) {

Vue.component('NoSSR', NoSSR);
Vue.component('LazyHydrate', LazyHydrate);

// AppFooter global to avoid CLS
Vue.component('AppFooter', AppFooter);

// Components should be multiple words error
// eslint-disable-next-line
Vue.component('Content', ContentSSR);

Vue.component('ClickoutAnchor', ClickoutAnchor);

const LowPriorityComponents = () =>
	import(/* webpackChunkName: "low-priority-components" */ '@/components/LowPriorityComponents.vue');

const route = useRoute();
const router = useRouter();
const { head } = storeToRefs(useMetaStore());
const { urlMetadata } = storeToRefs(useRoutingStore());
const { country, language, webLocale } = storeToRefs(useLanguageStore());
const { loading: userStatsLoading, permanentAudiences } = useUserStats();
const { initializeExperiments } = useExperimentInitialization();
const hideAppHeader = computed(() => route.name === 'app.streaming-charts');

if (process.client) useLoginFlow();

const { mutate, onError } = useMutation<RegisterSessionStartMutation, RegisterSessionStartMutationVariables>(
	RegisterSessionStartDocument
);
onError(error => {
	captureMessageForSentry('SSR App: Unable to fire Register Session Start Mutation', {
		where: '[SSRApp.vue] onMounted()',
		error,
	});
});

onMounted(async () => {
	const { proxy: instance } = getCurrentInstance() ?? { proxy: getVm() };
	//await initStoreWatchers(instance.$context as Context);

	watch(
		webLocale,
		async (webLocale, oldWebLocale) => {
			if (webLocale === oldWebLocale) return;
			await router.changeLocale(webLocale);
			await initLanguage({ ...instance.$context, webLocale });
		},
		{ immediate: false }
	);

	const { initCoreWebVitalsMonitor } = await import('@/helpers/tracking/cwv-helper');
	await initCoreWebVitalsMonitor({});

	nextTick(() => {
		const int = setInterval(() => {
			// remove loader
			const loader = document.querySelector('#loader');
			loader?.parentNode?.removeChild(loader);
			clearInterval(int);
		}, 50);

		// reactivate scroll on page rendered
		enableScroll();
	});

	const { TrackingHelper } = await import('@/helpers/tracking/tracking-helper');
	TrackingHelper.isInitializedPromise.then(async () => {
		// we wait until the user stats query is done
		await until(userStatsLoading).toBe(false);
		TrackingHelper.refreshPermanentAudiences(permanentAudiences.value);

		// route.fullPath is for Home as first page, which has incomplete urlMetadata
		if ((urlMetadata.value?.fullPath ?? route.fullPath) == null) return;

		await TrackingHelper.trackAudienceRefresh(permanentAudiences.value);
	});

	//need to initialize PASubgenres for it to populate trackers as SEO user
	TrackingHelper.isInitializedPromise.then(() => TrackingHelper.refreshPermanentAudiencesSubgenres([]));

	const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	// userStore.setTimeZone(browserTimeZone); // this causes interaction lag on safari browsers

	const registerMutation = await mutate({
		input: {
			platform: Platform.Web,
			language: language.value,
			country: country.value,
			timezone: browserTimeZone,
		},
	});

	const newExperiments: NewExperimentsType[] = (registerMutation?.data?.registerSessionStart.experiments ?? []).map(
		el => ({
			countries: el.countries,
			languages: el.languages,
			name: el.name,
			sample: el.sample,
			variant: el.variant ?? null,
		})
	);
	initializeExperiments(newExperiments ?? []);
});

expose({ head });

return { __sfc: true,LowPriorityComponents, route, router, head, urlMetadata, country, language, webLocale, userStatsLoading, permanentAudiences, initializeExperiments, hideAppHeader, mutate, onError, CanaryMarker, AppHeader, StyleInjector }
}

})